(() => {
  const aliases = [
    [/\bmercedes[- ]?benz\b|\bmercedes\b|\bbenz\b/g, ' mercedesbenz '],
    [/\bvolkswagen\b|\bvw\b/g, ' volkswagen '],
    [/\bbmw\s*3er\b/g, ' bmw 3 series '],
    [/\bgolf\s*viii\b/g, ' golf 8 '],
    [/\bgolf\s*vii\b/g, ' golf 7 '],
    [/\bgolf\s*vi\b/g, ' golf 6 '],
    [/\bgolf\s*iv\b/g, ' golf 4 '],
    [/\bmodel\s*([3syx])\b/g, ' model $1 '],
    [/\bskoda\b/g, ' skoda ']
  ];

  function normalize(value) {
    let result = String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\-_/(),]+/g, ' ');
    aliases.forEach(([pattern, replacement]) => {
      result = result.replace(pattern, replacement);
    });
    return result.replace(/\s+/g, ' ').trim();
  }

  function prepare(item) {
    const tags = Array.isArray(item.tags) ? item.tags.join(' ') : item.tags || '';
    const search = normalize([item.brand, ...(item.brands || []), item.vehicle, item.title, ...(item.codes || []), item.years, item.version, tags].join(' '));
    return { ...item, _search: search, _tokens: new Set(search.split(' ')), _brand: normalize(item.brand) };
  }

  function score(item, query) {
    const normalized = normalize(query);
    if (!normalized) return 0;
    const tokens = normalized.split(' ').filter(Boolean);
    let value = 0;
    if (item._search === normalized) value += 240;
    if (item._search.includes(normalized)) value += 120;
    if (item._brand === normalized) value += 80;
    for (const code of item.codes || []) {
      if (normalize(code) === normalized) value += 180;
      if (tokens.includes(normalize(code))) value += 120;
    }
    const matched = tokens.filter((token) => item._tokens.has(token) || item._search.includes(token));
    if (matched.length !== tokens.length && item._brand !== normalized) return 0;
    if (matched.length === tokens.length) value += 70 + matched.length * 12;
    value -= Math.min(40, Math.max(0, ((item.codes || []).length - 1) * 4));
    return Math.max(0, value);
  }

  function init(root) {
    const form = root.querySelector('[data-vehicle-search]');
    const input = root.querySelector('[data-vehicle-input]');
    const suggestions = root.querySelector('[data-vehicle-suggestions]');
    const resultsSection = root.querySelector('[data-results-section]');
    const resultsGrid = root.querySelector('[data-results-grid]');
    const status = root.querySelector('[data-results-status]');
    const empty = root.querySelector('[data-no-results]');
    const showMore = root.querySelector('[data-show-more]');
    const template = root.querySelector('[data-result-template]');
    const allBrands = root.querySelector('[data-all-brands]');
    let index = [];
    let currentResults = [];
    let visibleCount = 12;
    let ready = false;

    function renderCard(item) {
      const fragment = template.content.cloneNode(true);
      fragment.querySelectorAll('[data-card-url]').forEach((link) => link.href = item.url);
      fragment.querySelector('[data-card-brand]').textContent = item.brand;
      fragment.querySelector('[data-card-title]').textContent = item.vehicle;
      const image = fragment.querySelector('[data-card-image]');
      image.src = item.image ? (item.image.startsWith('//') ? `https:${item.image}` : item.image) : '';
      image.alt = item.image ? `${item.vehicle} Lenkradbezug` : '';
      const yearRow = fragment.querySelector('[data-card-year-row]');
      if (item.years) fragment.querySelector('[data-card-year]').textContent = item.years;
      else yearRow.hidden = true;
      fragment.querySelector('[data-card-version]').textContent = item.version || 'Lenkradform anhand der Produktbilder prüfen';
      return fragment;
    }

    function renderResults(query) {
      if (!ready) return;
      const normalized = normalize(query);
      input.value = query;
      closeSuggestions();
      if (!normalized) {
        resultsSection.hidden = true;
        empty.hidden = true;
        return;
      }
      currentResults = index
        .map((item) => ({ item, score: score(item, normalized) }))
        .filter((entry) => entry.score > 0)
        .sort((a, b) => b.score - a.score || a.item.vehicle.localeCompare(b.item.vehicle, 'de'))
        .map((entry) => entry.item);
      visibleCount = 12;
      resultsGrid.replaceChildren(...currentResults.slice(0, visibleCount).map(renderCard));
      const count = currentResults.length;
      status.textContent = count === 1 ? '1 Produkt gefunden' : `${count} Produkte gefunden`;
      resultsSection.hidden = count === 0;
      empty.hidden = count !== 0;
      showMore.hidden = count <= visibleCount;
      if (count) resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else empty.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function closeSuggestions() {
      suggestions.hidden = true;
      suggestions.replaceChildren();
      input.setAttribute('aria-expanded', 'false');
    }

    function renderSuggestions(query) {
      const normalized = normalize(query);
      if (!ready || normalized.length < 2) {
        closeSuggestions();
        return;
      }
      const seen = new Set();
      const matches = index
        .map((item) => ({ item, score: score(item, normalized) }))
        .filter((entry) => entry.score > 0)
        .sort((a, b) => b.score - a.score)
        .filter((entry) => {
          const label = `${entry.item.brand} ${entry.item.codes[0] || entry.item.vehicle}`;
          const key = normalize(label);
          if (seen.has(key)) return false;
          seen.add(key);
          entry.label = label;
          return true;
        })
        .slice(0, 8);
      if (!matches.length) {
        closeSuggestions();
        return;
      }
      suggestions.replaceChildren(...matches.map((entry) => {
        const li = document.createElement('li');
        li.setAttribute('role', 'option');
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = entry.label;
        button.addEventListener('click', () => renderResults(entry.label));
        li.append(button);
        return li;
      }));
      suggestions.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function renderBrands() {
      const map = new Map();
      index.forEach((item) => {
        if (!map.has(item.brand)) map.set(item.brand, item.collection);
      });
      const links = [...map.entries()]
        .sort(([a], [b]) => a.localeCompare(b, 'de'))
        .map(([brand, collection]) => {
          const link = document.createElement('a');
          link.href = collection;
          link.textContent = brand;
          return link;
        });
      allBrands.replaceChildren(...links);
    }

    let debounce;
    input.addEventListener('input', () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => renderSuggestions(input.value), 120);
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeSuggestions();
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      renderResults(input.value.trim());
    });
    showMore.addEventListener('click', () => {
      visibleCount += 12;
      resultsGrid.replaceChildren(...currentResults.slice(0, visibleCount).map(renderCard));
      showMore.hidden = currentResults.length <= visibleCount;
    });
    document.addEventListener('click', (event) => {
      if (!root.contains(event.target)) closeSuggestions();
    });

    fetch(root.dataset.indexUrl)
      .then((response) => {
        if (!response.ok) throw new Error('Vehicle index unavailable');
        return response.json();
      })
      .then((data) => {
        index = data.map(prepare);
        ready = true;
        renderBrands();
      })
      .catch(() => {
        status.textContent = 'Die Fahrzeugsuche ist vorübergehend nicht verfügbar.';
        empty.hidden = false;
      });
  }

  document.querySelectorAll('[data-vehicle-finder]').forEach(init);
})();
